import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { StayInfo } from '../api/addressService';

interface Address {
  mainAddress: string;
  detailAddress: string;
  isComplete: boolean;
  lat?: number;
  lng?: number;
  addressId?: string;
  stay?: StayInfo;
  selectedMethod?: 'gps' | 'manual' | 'screenshot';
}

interface AddressContextType {
  address: Address | null;
  setAddress: (address: Address) => void;
  setAddressId: (addressId: string) => void;
  clearAddress: () => void;
  isAddressSet: () => boolean;
  getAddressId: () => string | undefined;
  setSelectedMethod: (method: 'gps' | 'manual' | 'screenshot') => void;
  getSelectedMethod: () => 'gps' | 'manual' | 'screenshot' | undefined;
}

// 초기 상태는 로컬 스토리지에서 불러옴
const loadAddressFromStorage = (): Address | null => {
  try {
    const addressData = localStorage.getItem('userAddress');
    return addressData ? JSON.parse(addressData) : null;
  } catch (error) {
    console.error('Error loading address from localStorage:', error);
    return null;
  }
};

const AddressContext = createContext<AddressContextType | undefined>(undefined);

export const AddressProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [address, setAddressState] = useState<Address | null>(loadAddressFromStorage);

  // 개발 환경에서만 address 변경 추적 (디버깅 목적)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('📍 AddressContext: address state changed:', {
        address,
        timestamp: new Date().toISOString(),
        hasLat: !!address?.lat,
        hasLng: !!address?.lng,
        hasAddressId: !!address?.addressId,
        isComplete: address?.isComplete
      });
    }
  }, [address]);

  // 주소 설정
  const setAddress = (newAddress: Address) => {
    // 개발 환경에서만 setAddress 호출 추적
    if (process.env.NODE_ENV === 'development') {
      console.log('📍 AddressContext: setAddress called:', {
        newAddress,
        timestamp: new Date().toISOString()
      });
    }
    
    setAddressState(newAddress);
    try {
      localStorage.setItem('userAddress', JSON.stringify(newAddress));
    } catch (error) {
      console.error('Error saving address to localStorage:', error);
    }
  };

  // 주소 ID만 업데이트
  const setAddressId = (addressId: string) => {
    // 개발 환경에서만 setAddressId 호출 추적
    if (process.env.NODE_ENV === 'development') {
      console.log('📍 AddressContext: setAddressId called:', {
        addressId,
        currentAddress: address,
        timestamp: new Date().toISOString()
      });
    }
    
    if (address) {
      const updatedAddress = { ...address, addressId };
      setAddressState(updatedAddress);
      try {
        localStorage.setItem('userAddress', JSON.stringify(updatedAddress));
      } catch (error) {
        console.error('Error saving address to localStorage:', error);
      }
    }
  };

  // 주소 ID 조회
  const getAddressId = (): string | undefined => {
    return address?.addressId;
  };

  // 선택된 방법 설정 - 주소가 없을 때는 빈 객체를 생성하지 않음
  const setSelectedMethod = (method: 'gps' | 'manual' | 'screenshot') => {
    
    if (address) {
      // 기존 address가 있으면 업데이트
      const updatedAddress = { ...address, selectedMethod: method };
      setAddressState(updatedAddress);
      try {
        localStorage.setItem('userAddress', JSON.stringify(updatedAddress));
      } catch (error) {
        console.error('Error saving address to localStorage:', error);
      }
    } else {
      // address가 없으면 selectedMethod만 별도로 저장
      try {
        localStorage.setItem('selectedAddressMethod', method);
      } catch (error) {
        console.error('Error saving selectedAddressMethod to localStorage:', error);
      }
    }
  };

  // 선택된 방법 조회
  const getSelectedMethod = (): 'gps' | 'manual' | 'screenshot' | undefined => {
    const method = address?.selectedMethod;
    if (method) {
      return method;
    }
    
    // address에 selectedMethod가 없으면 별도로 저장된 값 확인
    try {
      const storedMethod = localStorage.getItem('selectedAddressMethod');
      return storedMethod as 'gps' | 'manual' | 'screenshot' | undefined;
    } catch (error) {
      console.error('Error loading selectedAddressMethod from localStorage:', error);
      return undefined;
    }
  };

  // 주소 초기화
  const clearAddress = () => {
    setAddressState(null);
    try {
      localStorage.removeItem('userAddress');
    } catch (error) {
      console.error('Error removing address from localStorage:', error);
    }
  };

  // 주소 설정 여부 확인
  const isAddressSet = () => {
    return !!address && address.isComplete;
  };

  const contextValue: AddressContextType = {
    address,
    setAddress,
    setAddressId,
    clearAddress,
    isAddressSet,
    getAddressId,
    setSelectedMethod,
    getSelectedMethod
  };

  return (
    <AddressContext.Provider value={contextValue}>
      {children}
    </AddressContext.Provider>
  );
};

// 훅 형태로 컨텍스트 사용
export const useAddress = (): AddressContextType => {
  const context = useContext(AddressContext);
  if (context === undefined) {
    throw new Error('useAddress must be used within an AddressProvider');
  }
  return context;
};
